// import axios from 'axios';

export default {
  namespaced: true,
  
  state: {
    payments: [],
    blockedPayments: [],
    auditLogs: [],
    walletBalances: [],
    loading: false,
    error: null,
    statusFilter: 'all',
    companyFilter: 'all',
    searchQuery: '',
    companies: ['Bank A', 'Bank B', 'Bank C'],
    showBlockModal: false,
    showUnblockModal: false,
    selectedPayment: null,
    blockReason: '',
    unblockReason: '',
  },
  
  getters: {
    filteredPayments: (state) => {
      let filtered = state.payments.filter(p => p.status !== 'blocked');
      
      // Status filtering
      if (state.statusFilter !== 'all') {
        filtered = filtered.filter(p => p.status === state.statusFilter);
      }
      
      // Company filtering
      if (state.companyFilter !== 'all') {
        filtered = filtered.filter(p => p.company === state.companyFilter);
      }
      
      // Search filtering
      if (state.searchQuery) {
        const query = state.searchQuery.toLowerCase();
        filtered = filtered.filter(p => 
          p.id.toLowerCase().includes(query) ||
          p.recipient.toLowerCase().includes(query) ||
          p.amount.toString().includes(query)
        );
      }
      
      return filtered;
    },
    
    getPaymentById: (state) => (id) => {
      return [...state.payments, ...state.blockedPayments].find(p => p.id === id);
    },
    
    getAuditLogsForPayment: (state) => (paymentId) => {
      return state.auditLogs.filter(log => log.paymentId === paymentId);
    },

    getBlockedPayments: (state) => { return state.blockedPayments },
  },
  
  mutations: {
    setPayments(state, payments) {
      state.payments = payments;
    },
    
    setBlockedPayments(state, payments) {
      state.blockedPayments = payments;
    },
    
    setAuditLogs(state, logs) {
      state.auditLogs = logs;
    },
    
    setWalletBalances(state, balances) {
      state.walletBalances = balances;
    },
    
    setLoading(state, isLoading) {
      state.loading = isLoading;
    },
    
    setError(state, error) {
      state.error = error;
    },
    
    setStatusFilter(state, filter) {
      state.statusFilter = filter;
    },
    
    setCompanyFilter(state, filter) {
      state.companyFilter = filter;
    },
    
    setSearchQuery(state, query) {
      state.searchQuery = query;
    },
    
    setShowBlockModal(state, show) {
      state.showBlockModal = show;
    },

    setShowUnblockModal(state, show) {
      state.showUnblockModal = show;
    },
    
    setSelectedPayment(state, payment) {
      state.selectedPayment = payment;
    },
    
    setBlockReason(state, reason) {
      state.blockReason = reason;
    },

    setUnblockReason(state, reason) {
      state.unblockReason = reason;
    },
    
    updatePaymentStatus(state, { id, status }) {
      const payment = state.payments.find(p => p.id === id);
      if (payment) {
        payment.status = status;
      }
    },

    blockPayment(state, payment) {
      const originalPayment = state.payments.find(p => p.id === payment.paymentId);
      state.payments = state.payments.filter(p => p.id !== payment.paymentId);

      const blockedPayment = {
        ...originalPayment,
        status: 'blocked',
        blockDate: new Date().toISOString().split('T')[0],
        blockreason: payment.reason,
        blockedBy: payment.blockedBy,
      };

      state.blockedPayments.push(blockedPayment);
    },

    unblockPayment(state, paymentId) {
      const paymentIndex = state.blockedPayments.findIndex(p => p.id === paymentId);
      if (paymentIndex !== -1) {
        const payment = { ...state.blockedPayments[paymentIndex] };

        delete payment.blockDate;
        delete payment.blockReason;
        delete payment.blockedBy;

        payment.status = 'pending';
        state.payments.push(payment);
        state.blockedPayments.splice(paymentIndex, 1);
      }
    },
    
    cancelPayment(state, paymentId) {
      const paymentIndex = state.blockedPayments.findIndex(p => p.id === paymentId);
      if (paymentIndex !== -1) {
        // Update status to cancelled
        state.blockedPayments[paymentIndex].status = 'cancelled';
      }
    },
    
    addAuditLog(state, log) {
      state.auditLogs.unshift(log);
    }
  },
  
  actions: {
    async fetchPayments({ commit }) {
      const payload = {
        blocked: false
      }

      try {
        const response = await axios.get(window.bankApi + `Payment/getPayments`, {
          params: {
            limit: payload.limit ? payload.limit : null,
            blocked: payload.blocked
          }
        });

        commit('setPayments', response.data.response);
        return response.data.response;
      } catch (error) {
        throw error;
      }
    },
    async fetchBlockedPayments(context, payload) {
      context.commit('setLoading', true);
      try {
        const response = await axios.get(window.bankApi + `Payment/getPayments`, {
          params: {
            blocked: true,
            BankId: payload.BankId ? payload.BankId : null,
            from: payload.from ? payload.from : null,
            to: payload.to ? payload.to : null,
          }
        });
        context.commit('setBlockedPayments', response.data.response);
        return response.data.response;
      } catch (error) {
        context.commit('setLoading', false);
        throw error;
      }
    },

    async fetchRefundedPayments(context, payload) {
      context.commit('setLoading', true);
      try {
        const response = await axios.get(bankApi + `walletstats/getRefunded`, {
          params: {
            bankid: payload.bankid ? payload.bankid : null,
            from: payload.from ? payload.from : null,
            to: payload.to ? payload.to : null,
          }
        });
        return response.data.response;
      } catch (error) {
        context.commit('setLoading', false);
        throw error;
      }
    },

    async fetchRefundPayments(context, payload) {
      context.commit('setLoading', true);
      try {
        const response = await axios.get(bankApi + `walletstats/getRefund`, {
          params: {
            bankid: payload.bankid ? payload.bankid : null,
            from: payload.from ? payload.from : null,
            to: payload.to ? payload.to : null,
          }
        });
        return response.data.response;
      } catch (error) {
        context.commit('setLoading', false);
        throw error;
      }
    },

    async fetchDepositPayments(context, payload) {
      context.commit('setLoading', true);
      try {
        const response = await axios.get(bankApi + `walletstats/getAllWallet`, {
          params: {
            bankid: payload.bankid ? payload.bankid : null,
            from: payload.from ? payload.from : null,
            to: payload.to ? payload.to : null,
          }
        });
        return response.data.response;
      } catch (error) {
        context.commit('setLoading', false);
        throw error;
      }
    },

    async processRefund(context, payload) {
      try {
        const response = await axios.get(bankApi + `Payment/RefundDeposit`, {
          params: {
            userid: payload.userid,
            KazangRef: payload.kazangRef,
          },
          headers: {
            "Api-Key": globalApiKey,
          }
        });

        if(response.status === 200){
          Swal.fire('Success', 'Refund was successfully refunded', 'success');
        }

        return response.data.response;
      } catch (error) {
        throw error;
      }
    },
    
    async fetchWalletBalances({ commit }) {
      commit('setLoading', true);
      try {
        // In a real app, you would fetch from an API
        // const response = await axios.get(`${apiBaseUrl}/wallets/balances`);
        
        // For now, use mock data
        const mockData = [
          {
            company: 'Bank A',
            balance: 75000
          },
          {
            company: 'Bank B',
            balance: 120000
          },
          {
            company: 'Bank C',
            balance: 45000
          }
        ];
        
        commit('setWalletBalances', mockData);
        commit('setError', null);
      } catch (error) {
        console.error('Error fetching wallet balances:', error);
        errorHandler.tomcatError(error)
      } finally {
        commit('setLoading', false);
      }
    },
    
    async fetchAuditLogs({ commit }) {
      commit('setLoading', true);
      try {
        // In a real app, you would fetch from an API
        // const response = await axios.get(`${apiBaseUrl}/payments/audit-logs`);
        
        // For now, use mock data
        const mockLogs = [
          {
            timestamp: '2023-10-19T09:15:00',
            action: 'Processed',
            paymentId: 'PAY-1003',
            performedBy: 'System',
            details: 'Automatic execution'
          },
          {
            timestamp: '2023-10-20T11:30:00',
            action: 'Processed',
            paymentId: 'PAY-1005',
            performedBy: 'Admin1',
            details: 'Manual execution'
          },
          {
            timestamp: '2023-10-18T14:20:00',
            action: 'Blocked',
            paymentId: 'PAY-1006',
            performedBy: 'Admin1',
            details: 'Suspicious transaction amount'
          },
          {
            timestamp: '2023-10-19T11:45:00',
            action: 'Blocked',
            paymentId: 'PAY-1007',
            performedBy: 'Admin2',
            details: 'Recipient verification pending'
          }
        ];
        
        commit('setAuditLogs', mockLogs);
        commit('setError', null);
      } catch (error) {
        console.error('Error fetching audit logs:', error);
        errorHandler.tomcatError(error)
      } finally {
        commit('setLoading', false);
      }
    },
    async processPayment(context, payment) {

      console.log(payment)
      const payload = {
        params: {
          userid: payment.usersighnedup,
          KazangRef: payment.agent,
        },
        headers: {
          "Api-Key": globalApiKey,
        }
      };

      try {
        $utils.showLoading();
        context.commit('setLoading', true);

        const res = await axios.post(`${bankApi}Payment/payAgent`, {}, payload);

        if (res.status === 200) {
          Swal.fire('Success', 'Payment successfully processed', 'success');
          return true;
        }

        window.location.reload();
        $utils.hideLoading();
        return false;
      } catch (error) {
        console.error('Error processing payment:', error);
        errorHandler.tomcatError(error);
        $utils.hideLoading();
        return false;
      } finally {
        $utils.hideLoading();
        context.commit('setLoading', false);
      }
    },
    async processBulkPayment({ commit, rootState }, payment) {
      // const ApiKey = localStorage.getItem('Api-Key');

      try {
        const res = await axios.post(`${bankApi}Payment/processPayments`, payment, {
          headers: {
            "Api-Key": globalApiKey,
          }
        });

        // if(res.status === 200) {
        //   Swal.fire('Success', 'Payments successfully processed', 'success');
        // }

        return res.data;
      } catch (error) {
        console.error('Error processing payment:', error);
        errorHandler.tomcatError(error)
        return false;
      }
    },
    
    showBlockModal({ commit }, payment) {
      commit('setSelectedPayment', payment);
      commit('setBlockReason', '');
      commit('setShowBlockModal', true);
    },
    
    hideBlockModal({ commit }) {
      commit('setShowBlockModal', false);
      commit('setSelectedPayment', null);
      commit('setBlockReason', '');
    },
    
    async confirmBlockPayment({ commit, state, rootState }) {
      if (!state.selectedPayment || !state.blockReason) {
        return false;
      }
      
      commit('setLoading', true);
      try {
        // In a real app, you would call an API
        // await axios.post(`${apiBaseUrl}/payments/${state.selectedPayment.id}/block`, {
        //   reason: state.blockReason
        // });
        
        const currentUser = rootState.user ? rootState.user.username : 'Admin';
        
        // Update local state
        commit('blockPayment', { 
          payment: state.selectedPayment,
          reason: state.blockReason,
          blockedBy: currentUser
        });
        
        // Add audit log
        commit('addAuditLog', {
          timestamp: new Date().toISOString(),
          action: 'Blocked',
          paymentId: state.selectedPayment.id,
          performedBy: currentUser,
          details: state.blockReason
        });
        
        // Hide modal
        commit('setShowBlockModal', false);
        commit('setSelectedPayment', null);
        commit('setBlockReason', '');
        
        return true;
      } catch (error) {
        console.error('Error blocking payment:', error);
        errorHandler.tomcatError(error)
        return false;
      } finally {
        commit('setLoading', false);
      }
    },

    async blockPayment({ commit, rootState }, payment) {
      commit('setLoading', true);
      const user = JSON.parse(localStorage.getItem('user'));
      try {

        let payload = {
          blockedby: user.id,
          paymentId: payment.paymentId,
          reason: payment.reason
        }

        await axios.post(`${bankApi}Payment/blockKazangPayment`, {}, {
          params: payload
        });

        // commit('blockPayment', payment);
        Swal.fire('Success', 'Payment was successfully blocked', 'success');

        return true;
      } catch (error) {
        console.error('Error unblocking payment:', error);
        errorHandler.tomcatError(error)
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    async unblockPayment({ commit, rootState }, payment) {
      const user = JSON.parse(localStorage.getItem('user'));
      commit('setLoading', true);
      try {

        let payload = {
          unblockedby: user.id,
          paymentId: payment.paymentId,
          reason: payment.reason
        }

        await axios.post(`${bankApi}Payment/unblockKazangPayment`, {}, {
          params: payload
        });

        commit('unblockPayment', payment.id);
        Swal.fire('Success', 'Payment was unsuccessfully blocked', 'success');

        return true;
      } catch (error) {
        console.error('Error unblocking payment:', error);
        errorHandler.tomcatError(error)
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    async cancelPayment({ commit, rootState }, payment) {
      commit('setLoading', true);
      try {
        
        const currentUser = rootState.user ? rootState.user.username : 'Admin';
        commit('cancelPayment', payment.id);

        commit('addAuditLog', {
          timestamp: new Date().toISOString(),
          action: 'Cancelled',
          paymentId: payment.id,
          performedBy: currentUser,
          details: 'Payment permanently cancelled'
        });
        
        return true;
      } catch (error) {
        console.error('Error cancelling payment:', error);
        errorHandler.tomcatError(error)
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    async exportStatement(context) {
      try {
        const ApiKey = localStorage.getItem("Api-Key");
        const bankId = context.rootGetters.userCompany;

        const queryParams = new URLSearchParams({
          from: context.rootGetters.getGlobalDateRange.from,
          to: context.rootGetters.getGlobalDateRange.to,
          BankId: bankId ? bankId : null,
        });

        const url = `${bankApi}Payment/getpayment/download-excel?${queryParams.toString()}`;

        const res = await axios.get(url, {
          headers: {
            "Api-Key": globalApiKey
          },
          responseType: 'blob',
        });

        const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' });
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        const today = new Date();
        const formattedDate = today.toISOString().slice(0, 10);
        link.setAttribute('download', `payment-report-${formattedDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        return true;
      } catch (error) {
        console.error("Error exporting statement:", error);
        errorHandler.tomcatError(error);
        return false;
      }
    },
    async exportKycStatement(context) {
      try {
        const ApiKey = localStorage.getItem("Api-Key");
        const bankId = context.rootGetters.userCompany;

        const queryParams = new URLSearchParams({
          from: context.rootGetters.getGlobalDateRange.from,
          to: context.rootGetters.getGlobalDateRange.to,
          BankId: bankId ? bankId : null,
        });

        const url = `${bankApi}signup/kazangall/download-excel?${queryParams.toString()}`;

        const res = await axios.get(url, {
          headers: {
            "Api-Key": 'f6fef6af-0fca-49fe-bec4-89ddb6d24502',
          },
          responseType: 'blob',
        });

        const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' });
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        const today = new Date();
        const formattedDate = today.toISOString().slice(0, 10);
        link.setAttribute('download', `kyc-report-${formattedDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        return true;
      } catch (error) {
        console.error("Error exporting statement:", error);
        errorHandler.tomcatError(error);
        return false;
      }
    }
  }
}; 